import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Collapse } from '@mui/material';
import TimerIcon from '@material-ui/icons/Timer';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Button from 'Components/UI/Button';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { formatDate, fullDateShort } from 'Consts/DateFormats';
import useMobileLayout from 'Hooks/useMobileLayout';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

const NAME_MAX_HEIGHT = 22;

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  rootDesktop: {
    flexBasis: '30%',
  },
  name: {
    color: theme.palette.text.primary,
    display: 'flex',
    flexGrow: 2,
  },
  time: {
    alignSelf: 'flex-start',
    color: theme.palette.text.primary,
    fontSize: '0.8em',
    display: 'flex',
    flexBasis: '25%',
  },
  wrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  itemLabelWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  nameContainer: {
  },
  nameCollapse: {
    display: 'flex',
    alignSelf: 'center',
    visibility: 'visible',
  },
  alignCenter: {
    alignSelf: 'center',
  },
  taskDueDate: {
    color: theme.palette.text.primary,
  },
  taskDueDateIcon: {
    marginRight: theme.spacing(0.5),
  },
});

function ListItemLabel({
  className,
  classes,
  resource,
  expand,
  setExpand,
}) {
  const ref = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const name = resource.attr('name');
  const dueDate = resource.attr('dueDate');
  const isCompleted = resource.attr('status') === 'completed';
  const isMobile = useMobileLayout();

  useEffect(() => {
    setHasOverflow(ref.current.clientHeight > NAME_MAX_HEIGHT);
  }, [ref]);

  return (
    <div className={classNames(classes.root, className, {
      [classes.rootDesktop]: !isMobile,
    })}
    >
      <IconButton
        color={'secondary'}
        disabled
        icon={(isCompleted) ? CheckCircleIcon : RadioButtonUncheckedIcon}
      />
      <div className={classes.wrapper}>
        <div className={classes.itemLabelWrapper}>
          <Collapse
            className={classNames(classes.nameCollapse)}
            collapsedSize={NAME_MAX_HEIGHT}
            in={expand}
            timeout={{
              enter: 200,
              exit: 0,
            }}
            style={{ width: '100%' }}
          >
            <Typography
              ref={ref}
              className={classes.name}
              color={'primary'}
              variant={'h5'}
              weight={'bold'}
              align={'left'}
            >
              {name}
            </Typography>
          </Collapse>
          {hasOverflow && (
          <Button
            size="small"
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              setExpand(!expand);
            }}
            variant="icon"
            className={classNames({
              [classes.alignCenter]: !expand,
            })}
          >
            {expand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
          )}
        </div>
        <Typography
          className={classes.taskDueDate}
          variant={'body1'}
          weight={'medium'}
          align={'left'}
        >
          <TimerIcon
            className={classes.taskDueDateIcon}
            fontSize={'small'}
          />
          {formatDate(dueDate, fullDateShort)}
        </Typography>
      </div>
    </div>
  );
}

ListItemLabel.defaultProps = {
  className: '',
};

ListItemLabel.propTypes = {
  resource: PropTypes.object.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  expand: PropTypes.bool.isRequired,
  setExpand: PropTypes.func.isRequired,
};

export default withStyles(styles)(ListItemLabel);
